/**
 * RegressionDashboard.jsx — SuperAdmin > More > Regression Tests.
 *
 * Surfaces the regression framework (services/regression) as a table:
 *   • One row per registered module.
 *   • Last-run status badge + functional / index / perf counters.
 *   • "Run Full Regression" button at the top + per-module "Run".
 *   • Drill-down dialog showing every test case with stdout/stderr tail.
 *   • Separate "Index Audit" + "Perf Audit" buttons for read-only quick checks.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  Database,
  Loader2,
  Play,
  RefreshCw,
  ShieldCheck,
  XCircle,
  ListChecks,
  ChevronRight,
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';

import {
  fetchModules,
  triggerRun,
  fetchRun,
  fetchIndexAudit,
  fetchPerfAudit,
} from '../../api/regression';

// ─────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    passed: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle2 },
    failed: { color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
    error: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertCircle },
    running: { color: 'bg-blue-100 text-blue-800 border-blue-300', icon: Loader2 },
    partial: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: AlertCircle },
    unknown: { color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock },
  };
  const cfg = map[status] || map.unknown;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-none border text-xs font-medium ${cfg.color}`}
      data-testid={`regression-status-${status}`}
    >
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {status || 'unknown'}
    </span>
  );
}

function MetricCell({ label, present, total, ok = true }) {
  if (total === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span
      className={`text-xs font-mono ${ok ? 'text-green-700' : 'text-red-700'}`}
    >
      {present}/{total}{' '}
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
export default function RegressionDashboard() {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [currentRun, setCurrentRun] = useState(null);
  const [openModuleKey, setOpenModuleKey] = useState(null);
  const [auditDialog, setAuditDialog] = useState(null); // 'index' | 'perf' | null
  const [auditData, setAuditData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchModules();
      setModules(d.modules || []);
      setLastRun({
        id: d.last_run_id,
        status: d.last_run_status,
        finished_at: d.last_run_finished_at,
      });
    } catch (e) {
      toast.error('Failed to load modules');
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Poll the active run.
  useEffect(() => {
    if (!currentRunId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetchRun(currentRunId);
        if (cancelled) return;
        setCurrentRun(r);
        if (r.status !== 'running') {
          setRunning(false);
          toast.success(`Run finished: ${r.status}`);
          load();
          return;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
      if (!cancelled) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [currentRunId, load]);

  const handleRun = async ({ moduleKey = null, includePerf = false } = {}) => {
    setRunning(true);
    setCurrentRun(null);
    try {
      const res = await triggerRun({
        modules: moduleKey ? [moduleKey] : null,
        includePerf,
      });
      setCurrentRunId(res.run_id);
      toast.info(`Regression run started: ${res.run_id.slice(0, 8)}…`);
    } catch (e) {
      setRunning(false);
      toast.error('Failed to start run');
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const openAudit = async (kind) => {
    setAuditDialog(kind);
    setAuditData(null);
    try {
      const d = kind === 'index' ? await fetchIndexAudit() : await fetchPerfAudit();
      setAuditData(d);
    } catch (e) {
      toast.error(`Failed to load ${kind} audit`);
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const openModuleDetail = (key) => setOpenModuleKey(key);
  const closeModuleDetail = () => setOpenModuleKey(null);

  const detailModule = (() => {
    if (!openModuleKey) return null;
    // Prefer current run, fall back to last-run-per-module from list.
    const fromCurrent = (currentRun?.modules || []).find(
      (m) => m.module_key === openModuleKey
    );
    if (fromCurrent) return fromCurrent;
    const m = modules.find((mm) => mm.key === openModuleKey);
    return m?.last_run || null;
  })();

  // ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="regression-dashboard">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> Regression Tests
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Functional · Indexing · Performance — every Self-Serve module
            registered in <code>services/regression/module_registry.py</code>{' '}
            is exercised here.
          </p>
          {lastRun?.finished_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Last full run: <StatusBadge status={lastRun.status} />{' '}
              {new Date(lastRun.finished_at).toLocaleString()}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={load}
              data-testid="regression-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => openAudit('index')}
              data-testid="regression-index-audit"
            >
              <Database className="h-4 w-4 mr-2" /> Index Audit
            </Button>
            <Button
              variant="outline"
              className="rounded-none"
              onClick={() => openAudit('perf')}
              data-testid="regression-perf-audit"
            >
              <Activity className="h-4 w-4 mr-2" /> Perf Audit
            </Button>
            <Button
              className="rounded-none"
              disabled={running}
              onClick={() => handleRun({ includePerf: true })}
              data-testid="regression-run-all"
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Full Regression
            </Button>
          </div>
          {running && currentRun && (
            <p className="text-xs text-muted-foreground">
              Run {currentRunId?.slice(0, 8)}… — status:{' '}
              <StatusBadge status={currentRun.status} />
            </p>
          )}
        </div>
      </div>

      {/* Modules table */}
      <Card className="rounded-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="h-4 w-4" /> Registered Modules
          </CardTitle>
          <CardDescription className="text-xs">
            Every module in the registry — append a <code>ModuleSpec(...)</code>{' '}
            in <code>module_registry.py</code> to add a new row.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Module</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Functional</th>
                  <th className="px-3 py-2">Indexes</th>
                  <th className="px-3 py-2">Perf</th>
                  <th className="px-3 py-2">Duration</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => {
                  const lr = m.last_run || {};
                  const s = lr.summary || {};
                  const status = lr.status || 'unknown';
                  const idxOk =
                    (s.indexes_total || 0) === 0 ||
                    (s.indexes_failed || 0) === 0;
                  const perfOk =
                    (s.perf_total || 0) === 0 ||
                    (s.perf_failed || 0) + (s.perf_errored || 0) === 0;
                  const funcOk = (s.functional_failed || 0) === 0;
                  return (
                    <tr
                      key={m.key}
                      className="border-b hover:bg-muted/20"
                      data-testid={`regression-row-${m.key}`}
                    >
                      <td className="px-3 py-3">
                        <div className="font-medium">{m.label}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {m.description || '—'}
                        </div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
                          {m.pytest_paths?.length || 0} test path{m.pytest_paths?.length === 1 ? '' : 's'} ·{' '}
                          {m.indexes_count} idx · {m.perf_queries_count} perf
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <StatusBadge status={status} />
                      </td>
                      <td className="px-3 py-3">
                        <MetricCell
                          label="passed"
                          present={s.functional_passed || 0}
                          total={
                            (s.functional_passed || 0) +
                            (s.functional_failed || 0) +
                            (s.functional_skipped || 0)
                          }
                          ok={funcOk}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <MetricCell
                          label="present"
                          present={
                            (s.indexes_total || 0) - (s.indexes_failed || 0)
                          }
                          total={s.indexes_total || 0}
                          ok={idxOk}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <MetricCell
                          label="ok"
                          present={
                            (s.perf_total || 0) -
                            (s.perf_failed || 0) -
                            (s.perf_errored || 0)
                          }
                          total={s.perf_total || 0}
                          ok={perfOk}
                        />
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {lr.duration_ms != null
                          ? `${Math.round(lr.duration_ms / 100) / 10}s`
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-none"
                            disabled={running}
                            onClick={() => handleRun({ moduleKey: m.key, includePerf: true })}
                            data-testid={`regression-run-${m.key}`}
                          >
                            <Play className="h-3 w-3 mr-1" /> Run
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-none"
                            onClick={() => openModuleDetail(m.key)}
                            data-testid={`regression-details-${m.key}`}
                          >
                            Details
                            <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ───────── Module Detail dialog ───────── */}
      <Dialog
        open={!!openModuleKey}
        onOpenChange={(o) => !o && closeModuleDetail()}
      >
        <DialogContent
          className="max-w-4xl rounded-none max-h-[85vh] overflow-y-auto"
          data-testid="regression-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {modules.find((m) => m.key === openModuleKey)?.label ||
                openModuleKey}
            </DialogTitle>
            <DialogDescription>
              {detailModule
                ? `Last run: ${detailModule.status} · ${detailModule.duration_ms} ms`
                : 'No run yet — click Run to execute.'}
            </DialogDescription>
          </DialogHeader>

          {detailModule && (
            <Tabs defaultValue="functional" className="mt-2">
              <TabsList className="rounded-none">
                <TabsTrigger value="functional" className="rounded-none">
                  Functional
                </TabsTrigger>
                <TabsTrigger value="indexes" className="rounded-none">
                  Indexes
                </TabsTrigger>
                <TabsTrigger value="perf" className="rounded-none">
                  Performance
                </TabsTrigger>
              </TabsList>

              <TabsContent value="functional" className="mt-3 space-y-3">
                {(detailModule.functional || []).map((f, i) => (
                  <Card key={i} className="rounded-none">
                    <CardHeader className="py-2">
                      <CardTitle className="text-sm font-mono">
                        {f.pytest_path}
                      </CardTitle>
                      <CardDescription className="text-xs">
                        exit {f.exit_code} · {f.total} cases · {f.passed} passed ·{' '}
                        <span className={f.failed ? 'text-red-700 font-medium' : ''}>
                          {f.failed} failed
                        </span>{' '}
                        · {f.skipped} skipped · {f.duration_ms}ms
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="py-1 space-y-1">
                      {(f.cases || []).map((c, j) => (
                        <div key={j} className="text-xs flex items-start gap-2">
                          <StatusBadge status={c.status} />
                          <div className="flex-1">
                            <div className="font-mono">
                              {c.classname ? `${c.classname}::` : ''}{c.name}
                              <span className="text-muted-foreground ml-2">
                                {c.duration_ms}ms
                              </span>
                            </div>
                            {c.message && (
                              <pre className="mt-1 p-2 bg-red-50 text-red-900 text-[11px] whitespace-pre-wrap break-all">
                                {c.message}
                              </pre>
                            )}
                          </div>
                        </div>
                      ))}
                      {f.stderr_tail && (
                        <details className="mt-2 text-xs">
                          <summary className="cursor-pointer text-muted-foreground">
                            stderr tail
                          </summary>
                          <pre className="mt-1 p-2 bg-muted text-[10px] whitespace-pre-wrap break-all max-h-40 overflow-auto">
                            {f.stderr_tail}
                          </pre>
                        </details>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </TabsContent>

              <TabsContent value="indexes" className="mt-3">
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground uppercase tracking-wide">
                      <th className="p-2">Collection</th>
                      <th className="p-2">Expected Index</th>
                      <th className="p-2">Unique?</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailModule.indexes || []).map((r, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2 font-mono">{r.collection}</td>
                        <td className="p-2 font-mono">{r.expected_index}</td>
                        <td className="p-2">{r.expected_unique ? 'yes' : 'no'}</td>
                        <td className="p-2">
                          {r.present ? (
                            <Badge className="bg-green-100 text-green-800 rounded-none">
                              ok
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 rounded-none">
                              missing
                            </Badge>
                          )}
                        </td>
                        <td className="p-2 text-muted-foreground">
                          {r.note || (r.actual_match_name && `name: ${r.actual_match_name}`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabsContent>

              <TabsContent value="perf" className="mt-3">
                {(detailModule.perf || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Perf was not included in the last run. Use Run Full
                    Regression with perf enabled, or click Perf Audit.
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-muted/40">
                      <tr className="text-left text-muted-foreground uppercase tracking-wide">
                        <th className="p-2">Query</th>
                        <th className="p-2">Collection</th>
                        <th className="p-2">Iter</th>
                        <th className="p-2">Median</th>
                        <th className="p-2">P95</th>
                        <th className="p-2">Budget</th>
                        <th className="p-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailModule.perf || []).map((p, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-2">{p.label}</td>
                          <td className="p-2 font-mono">{p.collection}</td>
                          <td className="p-2">{p.iterations}</td>
                          <td className="p-2">{p.median_ms}ms</td>
                          <td className="p-2 font-medium">{p.p95_ms}ms</td>
                          <td className="p-2 text-muted-foreground">
                            ≤ {p.budget_ms_p95}ms
                          </td>
                          <td className="p-2">
                            {p.error ? (
                              <Badge className="bg-orange-100 text-orange-800 rounded-none">
                                error
                              </Badge>
                            ) : p.within_budget ? (
                              <Badge className="bg-green-100 text-green-800 rounded-none">
                                ok
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 rounded-none">
                                slow
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* ───────── Audit dialog (index / perf quick check) ───────── */}
      <Dialog
        open={!!auditDialog}
        onOpenChange={(o) => !o && setAuditDialog(null)}
      >
        <DialogContent
          className="max-w-3xl rounded-none max-h-[80vh] overflow-y-auto"
          data-testid="regression-audit-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {auditDialog === 'index' ? 'Index Audit' : 'Performance Audit'}
            </DialogTitle>
            <DialogDescription>
              {auditDialog === 'index'
                ? 'Compares declared indexes vs MongoDB list_indexes(). Read-only.'
                : 'Times each registered PerfQuery and compares to its p95 budget. Read-only.'}
            </DialogDescription>
          </DialogHeader>
          {!auditData ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Running…
            </div>
          ) : (
            <div>
              <p className="text-xs text-muted-foreground mb-2">
                {Object.entries(auditData.summary || {})
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(' · ')}
              </p>
              {auditDialog === 'index' ? (
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground uppercase tracking-wide">
                      <th className="p-2">Collection</th>
                      <th className="p-2">Expected Index</th>
                      <th className="p-2">Unique?</th>
                      <th className="p-2">Present</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData.results || []).map((r, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2 font-mono">{r.collection}</td>
                        <td className="p-2 font-mono">{r.expected_index}</td>
                        <td className="p-2">{r.expected_unique ? 'yes' : 'no'}</td>
                        <td className="p-2">
                          {r.present ? (
                            <Badge className="bg-green-100 text-green-800 rounded-none">
                              ✓
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 rounded-none">
                              ✗ {r.note || 'missing'}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-muted/40">
                    <tr className="text-left text-muted-foreground uppercase tracking-wide">
                      <th className="p-2">Query</th>
                      <th className="p-2">Collection</th>
                      <th className="p-2">P95</th>
                      <th className="p-2">Budget</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(auditData.results || []).map((p, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2">{p.label}</td>
                        <td className="p-2 font-mono">{p.collection}</td>
                        <td className="p-2">{p.p95_ms}ms</td>
                        <td className="p-2 text-muted-foreground">
                          ≤ {p.budget_ms_p95}ms
                        </td>
                        <td className="p-2">
                          {p.error ? (
                            <Badge className="bg-orange-100 text-orange-800 rounded-none">
                              error
                            </Badge>
                          ) : p.within_budget ? (
                            <Badge className="bg-green-100 text-green-800 rounded-none">
                              ok
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 rounded-none">
                              slow
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
